"use client";

import type { JobRole, JobRoleType, Staff } from "@procision-software/database";
import { staffNameLastFirstTitle, type StaffFilterContext } from "@procision-software/mason";
import type { BoundFieldWithLegacyProps } from "@procision-software/ui";
import { BoundField, cn, useFilteredFieldProps } from "@procision-software/ui";
import { Select, SelectItem } from "@procision-software/ui/v2";
import type { ForwardedRef } from "react";
import { forwardRef, useCallback, useMemo, type ComponentPropsWithRef } from "react";
import type { FieldValues, Path } from "react-hook-form";
import { mergeRefs } from "react-merge-refs";
import { trpc } from "~/utils/trpc";

type Ref = HTMLButtonElement;

export type StaffSelectOption = Staff & {
  jobRoles: JobRole[];
};

type StaffSelectProps = (
  | {
      /**
       * Setting valueAs to "id" will use the staff ID as the value type and onValueChange result
       * @default "id"
       */
      valueAs?: "id" | undefined;
      value?: string | null;
      onValueChange?: (value: string | null) => void;
    }
  | {
      /**
       * Setting valueAs to "object" will use the full staff object as the value type and onValueChange result
       * @default "id"
       */
      valueAs: "object";
      value?: StaffSelectOption | null;
      onValueChange?: (value: StaffSelectOption | null) => void;
    }
) &
  Omit<
    ComponentPropsWithRef<typeof Select<StaffSelectOption>>,
    "value" | "onValueChange" | "renderValue" | "options" | "children"
  > & {
    jobRoleType?: JobRoleType;
    context?: StaffFilterContext;
    includeBlankValue?: boolean;
    options?: StaffSelectOption[];
  };

const StaffSelect = forwardRef<Ref, StaffSelectProps>(
  (
    {
      jobRoleType,
      context,
      valueAs,
      value,
      onValueChange,
      emptyContent = "No staff members found",
      loadingContent = "Loading...",
      enableSearch = true,
      includeBlankValue,
      options,
      className,
      ...props
    },
    ref
  ) => {
    const { data: allStaff, ...query } = trpc.staff.list.useQuery(
      {
        roleType: jobRoleType,
        context: context
          ? {
              practiceId: context.practiceId,
              facilityId: context.facilityId,
              activeOnly: context.activeOnly ?? true,
            }
          : { activeOnly: true },
      },
      {
        staleTime: 1000 * 60 * 15, // 15 mins
        select: (data) =>
          data.sort((a, b) => staffNameLastFirstTitle(a).localeCompare(staffNameLastFirstTitle(b))),
      }
    );

    const { data: selectedStaff } = trpc.staff.get.useQuery(
      { id: valueAs !== "object" && !!value?.length ? value : "" },
      { enabled: valueAs !== "object" && !!value?.length }
    );

    const selectedValue = useMemo(() => {
      const result = valueAs === "object" ? value : selectedStaff;
      if (!result) return null;
      if (!jobRoleType) return result;
      // If a job role type is specified, only return the selected value if it has that job role
      if (result.jobRoles.some((role) => role.type === jobRoleType)) return result;
      return null;
    }, [valueAs, selectedStaff, jobRoleType, value]);

    const staff = useMemo(() => {
      // Ensure the selected value is always included in the options list
      return [
        ...(selectedValue ? [selectedValue] : []),
        ...(options === undefined
          ? (allStaff?.filter((s) => s.id !== selectedValue?.id) ?? [])
          : options.filter((o) => o.id !== selectedValue?.id)),
      ];
    }, [selectedValue, options, allStaff]);

    const handleChange = useCallback(
      (value: StaffSelectOption | null) => {
        if (valueAs === "object") {
          onValueChange?.(value);
        } else {
          onValueChange?.(value?.id ?? null);
        }
      },
      [valueAs, onValueChange]
    );

    return (
      <Select<StaffSelectOption>
        ref={ref}
        value={selectedValue}
        onValueChange={handleChange}
        renderValue={staffNameLastFirstTitle}
        emptyContent={emptyContent}
        loading={query.isLoading}
        loadingContent={loadingContent}
        enableSearch={enableSearch}
        className={cn("sm:min-w-72", className)}
        {...props}
      >
        {includeBlankValue && (
          <SelectItem value={null} searchValue={"-"}>
            <span>-</span>
          </SelectItem>
        )}
        {staff.map((staff) => (
          <SelectItem key={staff.id} value={staff} searchValue={staffNameLastFirstTitle(staff)}>
            <div className="flex flex-wrap items-center gap-1">
              <span>{staffNameLastFirstTitle(staff)}</span>
              {staff.jobRoles.length > 0 && (
                <span className="text-xs opacity-70">
                  ({staff.jobRoles.map((role) => role.name).join(", ")})
                </span>
              )}
            </div>
          </SelectItem>
        ))}
      </Select>
    );
  }
);
StaffSelect.displayName = "StaffSelect";

type StaffSelectFieldProps<
  TFieldValues extends FieldValues,
  TFieldPath extends Path<TFieldValues>,
> = Omit<StaffSelectProps, "name"> &
  Omit<BoundFieldWithLegacyProps<TFieldValues, TFieldPath>, "children">;

const StaffSelectFieldInner = <
  TFieldValues extends FieldValues,
  TFieldPath extends Path<TFieldValues>,
>(
  props: StaffSelectFieldProps<TFieldValues, TFieldPath>,
  ref?: ForwardedRef<Ref>
) => {
  const { fieldProps, inputProps } = useFilteredFieldProps<
    TFieldValues,
    TFieldPath,
    StaffSelectFieldProps<TFieldValues, TFieldPath>
  >(props);

  return (
    <BoundField
      {...fieldProps}
      labelProps={{
        ...fieldProps.labelProps,
        //  size: inputProps.size
      }}
      render={({ field }) => (
        <StaffSelect
          {...inputProps}
          {...field}
          onValueChange={field.onChange}
          ref={mergeRefs([ref, field.ref])}
        />
      )}
    />
  );
};
StaffSelectFieldInner.displayName = "StaffSelectField";

const StaffSelectField = forwardRef(StaffSelectFieldInner) as <
  TFieldValues extends FieldValues,
  TFieldPath extends Path<TFieldValues>,
>(
  props: StaffSelectFieldProps<TFieldValues, TFieldPath> & {
    ref?: React.ForwardedRef<Ref>;
  }
) => ReturnType<typeof StaffSelectFieldInner>;

export { StaffSelect, StaffSelectField };
